import type { ChangeEvent, FC } from 'react';
import { useCallback } from 'react';

import FormField, { type FormFieldProps } from './FormField';

/** Characters allowed inside the input as the user types (digits + common phone punctuation). */
const ALLOWED_INPUT_CHARS = /[^0-9+\-()\s]/g;

/** Chilean mobile: 9 digits starting with 9 (no country code). Kept for explicit local validation. */
const CHILEAN_PHONE_REGEX = /^9\d{8}$/;

interface PhoneFieldProps extends Omit<FormFieldProps, 'value' | 'onChange' | 'type'> {
  /** Free-form phone string. May include `+`, digits, spaces, hyphens or parens. */
  value: string;
  /** Receives the cleaned input (allowed chars only). Use {@link normalizePhone} to canonicalize. */
  onChange: (value: string) => void;
}

const PhoneField: FC<PhoneFieldProps> = ({
  value,
  onChange,
  label = 'Phone',
  placeholder = '+56 9 1234 5678',
  hint,
  error,
  ...rest
}) => {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Strip disallowed chars but preserve the user's formatting (spaces, dashes, parens, leading +).
      const cleaned = e.target.value.replace(ALLOWED_INPUT_CHARS, '').slice(0, 24);
      onChange(cleaned);
    },
    [onChange]
  );

  return (
    <FormField
      {...rest}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      hint={hint ?? 'Incluye el código de país para números internacionales.'}
      error={error}
      slotProps={{
        ...rest.slotProps,
        htmlInput: {
          ...((typeof rest.slotProps?.htmlInput === 'object' && rest.slotProps?.htmlInput) || {}),
          inputMode: 'tel',
          autoComplete: 'tel',
          maxLength: 24,
        },
      }}
    />
  );
};

/**
 * Canonicalize a phone string to E.164-ish form: leading `+` (if any) followed by digits only.
 * Useful right before submitting to the backend.
 */
export const normalizePhone = (raw: string): string => {
  const trimmed = raw.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
};

/** Strict Chilean mobile validator: 9 digits, starts with 9 (no country code). */
export const isValidChileanPhone = (raw: string): boolean =>
  CHILEAN_PHONE_REGEX.test(normalizePhone(raw).replace(/^\+?56/, ''));

export default PhoneField;
